import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import { setSelectedProduct } from '../store/slices/appSlice';
import AddToCartButton from './AddToCartButton';
import { getCurrencySymbol } from '../helper/currencyHelper';
import { getProductCoverUrl } from '../helper/imageHelper';
import { useTranslation } from 'react-i18next';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

export const getAllProductImages = (p) => {
    if (!p) return ['/assets/images/thumbs/product-img1.png'];
    const images = [];

    const addImg = (url) => {
        if (url && typeof url === 'string' && url.startsWith('http') && !images.includes(url)) {
            images.push(url);
        }
    };

    // 1. CoverRef image FIRST
    if (p.coverRef && p.pictures && p.pictures[p.coverRef]) {
        const coverPic = p.pictures[p.coverRef];
        addImg(coverPic?.onlinePath || coverPic?.path);
    }

    // 2. isCover === true images
    if (p.pictures) {
        Object.values(p.pictures).forEach(pic => {
            if (pic?.isCover) {
                addImg(pic.onlinePath || pic.path);
            }
        });
        // 3. All other pictures in subcollection
        Object.values(p.pictures).forEach(pic => {
            if (typeof pic === 'string') addImg(pic);
            else addImg(pic?.onlinePath || pic?.path);
        });
    }

    // 4. Fallback fields
    if (p.coverUrl) addImg(p.coverUrl);
    if (p.onlinePath) addImg(p.onlinePath);
    if (typeof p.picture === 'string' && p.picture.startsWith('http')) addImg(p.picture);

    return images.length > 0 ? images : [getProductCoverUrl(p)];
};

const NextArrow = ({ onClick }) => (
    <button 
        type="button" 
        className="position-absolute end-0 top-50 translate-middle-y z-3 bg-white border shadow-sm rounded-circle w-36 h-36 flex-center text-gray-700 hover-bg-main-600 hover-text-white me-8"
        onClick={onClick}
        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
        aria-label="Next"
    >
        <i className="ph ph-caret-right text-lg"></i>
    </button>
);

const PrevArrow = ({ onClick }) => (
    <button 
        type="button" 
        className="position-absolute start-0 top-50 translate-middle-y z-3 bg-white border shadow-sm rounded-circle w-36 h-36 flex-center text-gray-700 hover-bg-main-600 hover-text-white ms-8"
        onClick={onClick}
        style={{ cursor: 'pointer', border: 'none', outline: 'none' }}
        aria-label="Previous"
    >
        <i className="ph ph-caret-left text-lg"></i>
    </button>
);

const ProductDetailModal = ({ product }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const catalog = useSelector((state) => state.app.catalog.value);

    const allImages = getAllProductImages(product);
    const [sliderRef, setSliderRef] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);

    if (!product) return null;

    const calculatePrice = (price, discount) => {
        if (!price) return 0;
        if (!discount || discount <= 0) return price;
        return price - (price * (discount / 100));
    };

    const finalPrice = calculatePrice(product.price, product.discount);

    const stockConfig = {
        available: { label: t('search.available'), color: 'success' },
        ondemand: { label: t('search.onDemand'), color: 'info' },
        ask: { label: t('search.ask'), color: 'warning' },
        transit: { label: t('search.inTransit'), color: 'primary' },
        soldout: { label: t('search.soldOut'), color: 'danger' }
    }[product.stock || 'available'] || { label: t('search.available'), color: 'success' };

    const sliderSettings = {
        dots: false,
        infinite: allImages.length > 1,
        speed: 350,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipe: true,
        draggable: true,
        touchMove: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        beforeChange: (current, next) => setActiveIdx(next),
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => dispatch(setSelectedProduct(null))}>
            <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content rounded-16 border-0 overflow-hidden position-relative">
                    <button 
                        type="button" 
                        className="btn-close position-absolute top-0 end-0 m-24 z-3" 
                        onClick={() => dispatch(setSelectedProduct(null))}
                    ></button>
                    <div className="modal-body p-32">
                        <div className="row g-24 align-items-center">
                            {/* Image Slider Column */}
                            <div className="col-md-6">
                                <div className="position-relative bg-gray-50 rounded-16 p-16 overflow-hidden">
                                    {allImages.length === 1 ? (
                                        <div className="flex-center p-16" style={{ height: '300px' }}>
                                            <img 
                                                src={allImages[0]} 
                                                alt={product.name} 
                                                className="mw-100 mh-100 object-fit-contain" 
                                                style={{ mixBlendMode: 'multiply' }}
                                            />
                                        </div>
                                    ) : (
                                        <Slider ref={setSliderRef} {...sliderSettings}>
                                            {allImages.map((imgUrl, index) => (
                                                <div key={index} className="outline-0">
                                                    <div className="flex-center p-16" style={{ height: '300px' }}>
                                                        <img 
                                                            src={imgUrl} 
                                                            alt={`${product.name}-${index}`} 
                                                            className="mw-100 mh-100 object-fit-contain" 
                                                            style={{ mixBlendMode: 'multiply' }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </Slider>
                                    )}
                                </div>

                                {/* Thumbnail Selector under Slider */}
                                {allImages.length > 1 && (
                                    <div className="d-flex align-items-center justify-content-center gap-8 overflow-x-auto mt-16 py-4">
                                        {allImages.map((imgUrl, index) => (
                                            <div 
                                                key={index} 
                                                className={`rounded-8 overflow-hidden cursor-pointer border transition-2 ${activeIdx === index ? 'border-main-600 border-2 shadow-xs' : 'border-gray-200 opacity-60 hover-opacity-100'}`}
                                                style={{ width: '54px', height: '54px', flexShrink: 0 }}
                                                onClick={() => sliderRef?.slickGoTo(index)}
                                            >
                                                <img src={imgUrl} alt={`${product.name}-thumb-${index}`} className="w-100 h-100 object-fit-cover" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Product Info Column */}
                            <div className="col-md-6">
                                {product.code && (
                                    <span className="badge bg-main-100 text-main-600 mb-8 d-inline-block">{product.code}</span>
                                )}
                                <h4 className="fw-bold text-gray-900 mb-12">{product.name}</h4>
                                <div className="d-flex justify-content-end mb-8">
                                    <span className={`badge bg-${stockConfig.color}-50 text-${stockConfig.color}-600 border border-${stockConfig.color}-200 rounded-pill px-12 py-4 text-xs fw-bold`}>
                                        {stockConfig.label}
                                    </span>
                                </div>
                                <div className="d-flex align-items-baseline gap-12 mb-16">
                                    <h3 className="text-main-600 fw-bold mb-0">
                                        {getCurrencySymbol(catalog)}{finalPrice.toFixed(2)}
                                    </h3>
                                    {product.discount > 0 && (
                                        <span className="text-gray-400 text-decoration-line-through">
                                            {getCurrencySymbol(catalog)}{product.price?.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                <p className="text-gray-600 mb-24" style={{ whiteSpace: 'pre-line' }}>
                                    {product.description || t('productDetail.description')}
                                </p>

                                {(product.stock === 'ask' || product.stock === 'ondemand') && (
                                    <div className={`alert bg-${stockConfig.color}-50 border border-${stockConfig.color}-200 text-${stockConfig.color}-700 rounded-8 p-12 mb-24 d-flex gap-8 align-items-start`}>
                                        <i className="ph ph-info mt-2"></i>
                                        <span className="text-sm fw-medium">
                                            {product.stock === 'ask' ? t('search.askDesc') : t('search.onDemandDesc')}
                                        </span>
                                    </div>
                                )}
                                <AddToCartButton 
                                    productId={product.id} 
                                    className="btn btn-main w-100 rounded-pill py-12 flex-center gap-8 fw-bold"
                                    onAdd={() => setTimeout(() => dispatch(setSelectedProduct(null)), 800)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailModal;
